// ============================================================================
// ADR-0024 §7 — completeness & consistency checklist for a sectioned report
// ============================================================================
// Pure function over what the editor already holds (component sections, the
// workspace, Corporate Facts, live data). Every item ALERTS — nothing blocks a
// save or a print; the auditor keeps editorial control.

import type {
  ReportLiveData,
  ReportSectionDefinition,
  ReportWorkspaceData,
} from 'api/types/fsa/reportData.types';
import type { CorporateFactsData } from 'api/types/fsa/corporateFacts.types';
import {
  answerOf,
  collectQuestions,
  computeDrift,
  usedDataKeys,
  derivedFromFacts,
  isOmitted,
  resolveVariant,
  TABLE_LABELS,
} from './reportSections';

export type ChecklistSeverity = 'warning' | 'info';

export interface ChecklistItem {
  severity: ChecklistSeverity;
  /** Section to jump to (template code or added local_id); absent for report-level items. */
  sectionCode?: string;
  message: string;
}

const GUIDANCE = /\[[^\]]{1,120}\]/g;

export function buildChecklist(
  defs: ReportSectionDefinition[],
  ws: ReportWorkspaceData,
  facts: CorporateFactsData | undefined,
  live: ReportLiveData | null | undefined,
  /** ADR-0024 A-1 — which side signs: the board (Director's Report) or the auditor (Auditor's Report, CARO). */
  signingSide: 'board' | 'auditor' | 'none' = 'board',
): ChecklistItem[] {
  const items: ChecklistItem[] = [];
  const sectionData = ws.section_data ?? {};
  const fields = ws.resolved_fields ?? {};
  const tables = ws.resolved_tables ?? {};

  // ---- per section: hidden mandatory, missing values, leftover guidance ----
  const printable: Array<{ code: string; title: string; content: unknown }> = [];
  for (const def of defs) {
    const ov = sectionData[def.code];
    const title = ov?.custom_title ?? def.title;
    const resolved = resolveVariant(def, ov, facts);
    if (isOmitted(def, resolved)) continue;
    if (ov?.is_hidden) {
      if (def.optional !== true) {
        items.push({
          severity: 'warning',
          sectionCode: def.code,
          message: `“${title}” is hidden but is a required disclosure${def.statutory_refs?.length ? ` (${def.statutory_refs.join(', ')})` : ''}.`,
        });
      }
      continue;
    }
    printable.push({ code: def.code, title, content: ov?.content ?? resolved.content });
  }
  for (const a of ws.added_sections ?? []) {
    const ov = sectionData[a.local_id];
    if (ov?.is_hidden) continue;
    printable.push({ code: a.local_id, title: ov?.custom_title ?? a.title, content: ov?.content ?? a.content });
  }

  for (const s of printable) {
    const missing = new Set<string>();
    const guidance = new Set<string>();
    const emptyTables = new Set<string>();
    walk(s.content, node => {
      if (node.type === 'fieldChip') {
        const path = node.attrs?.path as string | undefined;
        if (path && !fields[path]) missing.add((node.attrs?.label as string) ?? path);
      } else if (node.type === 'dataTable') {
        const src = node.attrs?.source as string | undefined;
        const t = src ? tables[src] : undefined;
        if (t && t.rows.length === 0 && !t.emptyText && t.emptyNote) {
          emptyTables.add(`${TABLE_LABELS[src!] ?? src}: ${t.emptyNote}`);
        }
        // ADR-0024 N-5 — a table's review points (e.g. an unexplained ratio
        // change above 25%), from the CURRENT values so a reason just given clears it.
        const current = src ? (live?.tables?.[src] ?? t) : undefined;
        for (const w of current?.warnings ?? []) emptyTables.add(w);
      } else if (node.type === 'text' && typeof node.text === 'string') {
        for (const m of node.text.match(GUIDANCE) ?? []) guidance.add(m);
      }
    });
    if (missing.size > 0) {
      items.push({ severity: 'warning', sectionCode: s.code, message: `“${s.title}”: no value yet for ${[...missing].join(', ')}.` });
    }
    if (guidance.size > 0) {
      const list = [...guidance];
      items.push({
        severity: 'warning',
        sectionCode: s.code,
        message: `“${s.title}” still contains guidance text ${list.slice(0, 2).map(g => truncate(g, 60)).join(', ')}${list.length > 2 ? ` and ${list.length - 2} more` : ''} — replace or remove it.`,
      });
    }
    for (const t of emptyTables) items.push({ severity: 'warning', sectionCode: s.code, message: `“${s.title}”: ${t}` });
  }

  // ---- questions: unanswered, conflicts, suggestions ----------------------
  // A Direct note (edited as plain text) has no questionnaire to answer.
  const suggestions = live?.suggestions ?? {};
  let unansweredPlain = 0;
  let unansweredAudit = 0;
  for (const q of ws.authoring_mode === 'DIRECT' ? [] : collectQuestions(defs)) {
    const firstSection = defs.find(d => d.applicability?.question === q.code)?.code;
    const answer = answerOf(q, facts);
    const derived = derivedFromFacts(q.deriveFrom, facts);
    const suggestion = suggestions[q.code];

    // ADR-0024 A-1 — report-question (audit scope) answers: one summary line;
    // the question panel above the editor lists each one.
    // ADR-0024 N-2 — note questions (scope "note") are answered in the same panel.
    if (q.scope === 'audit' || q.scope === 'note') {
      if (answer === undefined || (q.scope === 'note' && facts?.note_answers_carried?.includes(q.code))) unansweredAudit++;
      else if (typeof answer === 'boolean' && suggestion && suggestion.answer !== answer) {
        items.push({
          severity: 'warning',
          sectionCode: firstSection,
          message: `“${q.prompt}” is answered ${answer ? 'Yes' : 'No'}, but the saved figures suggest ${suggestion.answer ? 'Yes' : 'No'}: ${suggestion.reason}.`,
        });
      }
      continue;
    }

    if (answer === false && derived) {
      items.push({
        severity: 'warning',
        sectionCode: firstSection,
        message: `“${q.prompt}” is answered No, but entries are recorded in Corporate Facts (${q.deriveFrom?.replace(/_/g, ' ')}).`,
      });
    }
    if (typeof answer === 'boolean' && suggestion && suggestion.answer !== answer) {
      items.push({
        severity: 'warning',
        sectionCode: firstSection,
        message: `“${q.prompt}” is answered ${answer ? 'Yes' : 'No'}, but the saved figures suggest ${suggestion.answer ? 'Yes' : 'No'}: ${suggestion.reason}.`,
      });
    }
    if (answer === undefined && !derived) {
      if (suggestion) {
        items.push({
          severity: 'warning',
          sectionCode: firstSection,
          message: `“${q.prompt}” is not answered — the saved figures suggest ${suggestion.answer ? 'Yes' : 'No'} (${suggestion.reason}). Answer it in Corporate Facts.`,
        });
      } else {
        unansweredPlain++;
      }
    }
  }
  // One line, not one per question — each unanswered question already shows
  // its default on the section strip.
  if (unansweredPlain > 0) {
    items.push({
      severity: 'info',
      message: `${unansweredPlain} applicability question(s) not answered in Corporate Facts — each section uses its default text.`,
    });
  }
  if (unansweredAudit > 0) {
    items.push({
      severity: 'warning',
      message: `${unansweredAudit} report question(s) not answered — the defaults are printing. Answer them in "Report questions" above.`,
    });
  }

  // ---- ADR-0024 A-3 — cross-document: the audit opinion vs the Board's Report.
  // s.134(3)(f): a qualified / adverse / disclaimer opinion needs the Board's
  // explanation. The opinion is answered in the Auditor's Report; the Board's
  // Report question lives in Corporate Facts.
  const opinion = facts?.audit_answers?.OPINION_TYPE;
  const qualQuestion = collectQuestions(defs).find(q => q.code === 'AUDIT_QUALIFICATIONS');
  if (qualQuestion && typeof opinion === 'string' && opinion !== 'UNMODIFIED'
      && answerOf(qualQuestion, facts) !== true) {
    items.push({
      severity: 'warning',
      sectionCode: defs.find(d => d.applicability?.question === 'AUDIT_QUALIFICATIONS')?.code,
      message: `The Auditor's Report gives a ${opinion.toLowerCase()} opinion, but "${qualQuestion.prompt}" is not answered Yes — the Board must explain the auditor's qualification (s.134(3)(f)).`,
    });
  }

  // ---- ADR-0024 R-4b — carried from the previous year ---------------------
  // Sections still holding last year's wording (only those that print).
  const carriedSections = new Set(ws.carried_from_py ?? []);
  const carriedPrinted = printable.filter(s => carriedSections.has(s.code));
  if (carriedPrinted.length > 0) {
    items.push({
      severity: 'warning',
      sectionCode: carriedPrinted[0].code,
      message: carriedPrinted.length === 1
        ? `“${carriedPrinted[0].title}” still has last year's wording — review it (or Mark reviewed).`
        : `${carriedPrinted.length} sections still have last year's wording (marked PY) — review each, starting with “${carriedPrinted[0].title}”.`,
    });
  }
  const unconfirmed = (facts?.applicability_carried ?? []).length;
  if (unconfirmed > 0) {
    items.push({
      severity: 'warning',
      message: `${unconfirmed} applicability answer(s) were carried from the previous year and not confirmed — confirm them in Corporate Facts.`,
    });
  }
  const rptNoCy = (facts?.related_party_transactions ?? []).filter(r => r.value_cy == null).length;
  if (rptNoCy > 0) {
    items.push({
      severity: 'warning',
      message: `${rptNoCy} related-party transaction(s) have no current-year value — enter them in Corporate Facts (Form AOC-2 prints them).`,
    });
  }

  // ---- report level --------------------------------------------------------
  if (live && signingSide === 'board' && (live.boardSignerCount ?? 0) === 0) {
    items.push({ severity: 'warning', message: 'No signing directors — add them to the board side of the engagement’s Signature Panel.' });
  }
  if (live && signingSide === 'auditor') {
    if ((live.auditorSignerCount ?? 0) === 0) {
      items.push({ severity: 'warning', message: 'No signing partner — add the audit firm and partner to the auditor side of the engagement’s Signature Panel.' });
    } else if ((live.auditorMissingUdinCount ?? 0) > 0) {
      items.push({ severity: 'warning', message: 'UDIN not entered for the signing partner — add it in the engagement’s Signature Panel before issuing the report.' });
    }
  }
  const drift = computeDrift(ws, live, usedDataKeys(defs, ws, facts));
  if (drift.length > 0) {
    items.push({ severity: 'info', message: `${drift.length} value(s) used in the text changed since it was saved — use Refresh all to update them.` });
  }

  return items.sort((a, b) => (a.severity === b.severity ? 0 : a.severity === 'warning' ? -1 : 1));
}

interface TipTapNode {
  type?: string;
  text?: string;
  attrs?: Record<string, unknown>;
  content?: TipTapNode[];
}

function walk(doc: unknown, visit: (n: TipTapNode) => void) {
  const stack: unknown[] = [doc];
  while (stack.length) {
    const n = stack.pop();
    if (!n || typeof n !== 'object') continue;
    const node = n as TipTapNode;
    visit(node);
    if (Array.isArray(node.content)) for (const c of node.content) stack.push(c);
  }
}

function truncate(s: string, max: number) {
  return s.length > max ? `${s.slice(0, max - 1)}…]` : s;
}
